import { clientPath } from "@/lib/record-paths";

/**
 * The in-app destination for a notification's `→` follow affordance, derived
 * from its `event_type` and `entity_type` / `entity_id`. Returns `null` when
 * the notification has no navigable destination — the row then renders without
 * a follow link.
 */
export function notificationHref(
  eventType: string,
  entityType: string | null,
  entityId: string | null,
): string | null {
  // The morning briefing summarizes the whole worklist rather than one entity:
  // following it opens Maxwell chat, which asks for the briefing and renders
  // the complete list with a link per item.
  if (eventType === "maxwell.morning_briefing") {
    return `/maxwell/chat?ask=${encodeURIComponent("Give me my morning briefing")}`;
  }
  if (!entityId) return null;
  switch (entityType) {
    case "deal":
      return `/deals/${entityId}`;
    case "customer":
      return clientPath(entityId);
    // A contact has no standalone page; its ref id is its customer's id.
    case "contact":
      return clientPath(entityId);
    // Autonomy run → the Activity tab, focused on this run via the ?run=
    // query param.
    case "agent_run":
      return `/maxwell/activity?run=${entityId}`;
    // A filed capability proposal lands on the super-admin backlog page —
    // the notification is only ever addressed to the super-admin who filed
    // it, so the route's super-admin gate never blocks the follow.
    case "capability_proposal":
      return "/settings/capability-proposals";
    // User lifecycle events (invited/activated/suspended/reinstated) land on
    // the Users admin page (Global area), where the named user's row lives.
    case "user":
      return "/users";
    // Deliberately no "rfq" case: the RFQ detail route is
    // `/energy-advisor/:customerId/rfqs/:rfqId` and needs a customer id that
    // outbox-enqueued rfq events never carry — building `/rfqs/${entityId}`
    // here would be a dead link. Inbox rows for rfq-linked events show the
    // improved title/body but no follow arrow until entity resolution grows
    // a customer id.
    default:
      return null;
  }
}
